import React, { useState } from 'react';
import {
    ActionIcon,
    Box,
    Button,
    Group,
    Popover,
    Text,
    Slider,
    Divider,
    UnstyledButton,
} from '@mantine/core';
import {
    IconChevronDown,
    IconZoom,
    IconMinus,
    IconPlus,
    IconFocusCentered,
} from '@tabler/icons-react';
import {
    TABLE_ZOOM_DEFAULT,
    TABLE_ZOOM_MAX,
    TABLE_ZOOM_MIN,
} from '../../hooks/useTableZoom';
import { usePageZoom } from '../../context/PageZoomContext';

type TableZoomControlsProps = {
    zoomLevel: number;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomReset: () => void;
    onZoomChange?: (value: number) => void;
    canZoomIn?: boolean;
    canZoomOut?: boolean;
};

function ZoomControlsPanel({
    zoomLevel,
    onZoomIn,
    onZoomOut,
    onZoomReset,
    onZoomChange,
    canZoomIn = zoomLevel < TABLE_ZOOM_MAX,
    canZoomOut = zoomLevel > TABLE_ZOOM_MIN,
}: TableZoomControlsProps) {
    const zoomPercent = Math.round(zoomLevel * 100);
    const isDefaultZoom = zoomLevel === TABLE_ZOOM_DEFAULT;

    return (
        <Box className="flex flex-col min-w-[240px] p-1">
            <Group justify="space-between" mb="xs" px={4}>
                <Text size="sm" fw={500} c="dark.7">
                    Zoom Level
                </Text>
                <Text size="sm" fw={600} c="primary">
                    {zoomPercent}%
                </Text>
            </Group>

            <Group gap="xs" wrap="nowrap" align="center">
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={onZoomOut}
                    disabled={!canZoomOut}
                    aria-label="Zoom out"
                    radius="xl"
                >
                    <IconMinus size={16} stroke={2} />
                </ActionIcon>

                <Slider
                    flex={1}
                    value={zoomLevel}
                    min={TABLE_ZOOM_MIN}
                    max={TABLE_ZOOM_MAX}
                    step={0.1}
                    label={null}
                    onChange={onZoomChange}
                    color="primary"
                    size="sm"
                    thumbSize={16}
                />

                <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={onZoomIn}
                    disabled={!canZoomIn}
                    aria-label="Zoom in"
                    radius="xl"
                >
                    <IconPlus size={16} stroke={2} />
                </ActionIcon>
            </Group>

            <Divider my="sm" color="gray.2" />

            <UnstyledButton
                onClick={onZoomReset}
                disabled={isDefaultZoom}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium transition-colors hover:bg-gray-50 disabled:text-gray-400 disabled:hover:bg-transparent"
            >
                <IconFocusCentered size={16} stroke={2} />
                <span>Reset to default (100%)</span>
            </UnstyledButton>
        </Box>
    );
}

export function TableZoomControls({
    zoomLevel,
    onZoomIn,
    onZoomOut,
    onZoomReset,
    onZoomChange,
    canZoomIn,
    canZoomOut,
}: TableZoomControlsProps) {
    const [opened, setOpened] = useState(false);
    const zoomPercent = Math.round(zoomLevel * 100);

    return (
        <Box className="inline-flex shrink-0">
            <Popover
                opened={opened}
                onChange={setOpened}
                onDismiss={() => setOpened(false)}
                position="top-end"
                withArrow
                shadow="lg"
                offset={8}
                zIndex={500}
                radius="md"
            >
                <Popover.Target>
                    <Button
                        type="button"
                        variant="default"
                        size="sm"
                        radius="md"
                        className="font-medium border-gray-300 shadow-sm hover:bg-gray-50"
                        leftSection={
                            <IconZoom
                                size={16}
                                stroke={2}
                                className="text-gray-600"
                            />
                        }
                        rightSection={
                            <IconChevronDown
                                size={14}
                                className={`text-gray-500 transition-transform duration-150 ${
                                    opened ? 'rotate-180' : ''
                                }`}
                            />
                        }
                        onClick={() => setOpened(current => !current)}
                        aria-label={`Zoom: ${zoomPercent}%. Open zoom controls`}
                        aria-expanded={opened}
                    >
                        {zoomPercent}%
                    </Button>
                </Popover.Target>
                <Popover.Dropdown className="p-2 border-gray-200">
                    <ZoomControlsPanel
                        zoomLevel={zoomLevel}
                        onZoomIn={onZoomIn}
                        onZoomOut={onZoomOut}
                        onZoomReset={onZoomReset}
                        onZoomChange={onZoomChange}
                        canZoomIn={canZoomIn}
                        canZoomOut={canZoomOut}
                    />
                </Popover.Dropdown>
            </Popover>
        </Box>
    );
}

export function PageZoomControls() {
    const {
        zoomLevel,
        handleZoomIn,
        handleZoomOut,
        handleZoomReset,
        handleZoomChange,
        canZoomIn,
        canZoomOut,
    } = usePageZoom();

    return (
        <TableZoomControls
            zoomLevel={zoomLevel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={handleZoomReset}
            onZoomChange={handleZoomChange}
            canZoomIn={canZoomIn}
            canZoomOut={canZoomOut}
        />
    );
}
